import CustomList from '@/components/global/list/CustomList.jsx';
import { FunctionField, TopToolbar, usePermissions, useGetOne, useResourceContext, Edit, useGetIdentity, useTranslate } from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { Box, Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { styled } from '@mui/material/styles';
import UnassignedField from '@/components/global/fields/UnassignedField.jsx';
import React, { useMemo } from 'react';
import DeviceServiceRequestStateField from '@/components/device/DeviceServiceRequestStateField.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import {
    deviceStateChoices,
    provinceChoices,
    serviceRequestPriorityChoices,
    serviceRequestStatusChoices,
    errorTypeChoices,
    serviceRequestSourceTypeChoices,
} from '@/components/global/options/selectOptions.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import PropTypes from 'prop-types';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { DeviceIssueErrorField } from '@/pages/deviceIssue.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import DeviceServiceStatusField from '@/components/device/DeviceServiceStatusField';
import DeviceCollectionPointField from '@/components/device/DeviceCollectionPointField.jsx';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { ServiceShow } from '@/components/service/ServiceShow.jsx';
import { ServiceRequestIdField } from '@/components/service/list/ServiceRequestIdField';
import { ServiceRequestUsersField } from '@/components/service/list/ServiceRequestUsersField';
import { ServiceRequestStatusField } from '@/components/service/list/ServiceRequestStatusField';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import { ServiceForm } from '@/components/service/forms/ServiceForm.jsx';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { ServiceBulkActionButtons } from '@/components/global/buttons/bulkButtons.jsx';
import { ServiceBulkActionForm } from '@/components/service/ServiceBulkActionForm.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import CachedDataActionButton from '@/components/global/buttons/CachedDataActionButton.jsx';

const searchBar = <SearchBar key='search' source='value' alwaysOn />;

const getServiceRegularFilters = (isMobile, translate) => [
    <CustomSelectArrayInput
        key='filter_status'
        source='status_in'
        label={translate('common.status')}
        choices={serviceRequestStatusChoices(translate)}
        alwaysOn
        forceDesktopOnMobile={true}
    />,
    <CustomSelectArrayInput
        key='filter_type'
        source='type_in'
        label={translate('resources.serviceRequest.labels.typeLabel')}
        choices={errorTypeChoices(translate)}
        alwaysOn={isMobile}
        forceDesktopOnMobile={true}
    />,
    <CustomSelectArrayInput
        key='filter_priority'
        source='priority_in'
        label={translate('common.priorityLabel')}
        choices={serviceRequestPriorityChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <CustomSelectArrayInput
        key='filter_device_state'
        source='device->state_in'
        label={translate('resources.serviceRequest.labels.deviceStateTitle')}
        choices={deviceStateChoices}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <CustomSelectArrayInput
        key='filter_province'
        source='collectionPoint->address->province_in'
        label={translate('common.province')}
        choices={provinceChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <ReferenceArrayFilter
        key='filter_client'
        source='collectionPoint->contractorId_in'
        label={translate('common.contractor')}
        reference='contractor'
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <ReferenceArrayFilter
        key='collectionPoint->id_in'
        source='collectionPoint->id_in'
        label={translate('common.collectionPoint')}
        optionText={choice => choice.customId}
        reference='collection-point'
        searchFields={['customId']}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <FromToFilterComponent
        key='createdAt'
        source='createdAt_range'
        label={translate('common.createdAtFilter')}
        type='date'
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <FromToFilterComponent
        key='updatedAt'
        source='updatedAt_range'
        label={translate('common.updatedAtFilter')}
        type='date'
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <CustomSelectArrayInput
        key='sourceType'
        source='sourceType_in'
        label={translate('common.source')}
        choices={serviceRequestSourceTypeChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
];

const getAllFilters = (isMobile, translate) => [
    ...getServiceRegularFilters(isMobile, translate),
    <ReferenceArrayFilter
        key='filter_user'
        source='coordinatorId+servicemanId_in'
        label={translate('resources.serviceRequest.labels.serviceman')}
        reference='user'
        optionText={choice => `${choice.firstName} ${choice.lastName}`}
        searchFields={['firstName', 'lastName']}
        filter={{ role_in: ['ROLE_SERVICE_MASTER', 'ROLE_SERVICE'] }}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
];

const StyledServiceList = ({ children, ...props }) => {
    return (
        <CustomList {...props}>
            <Box className='responsive-table service-list'>{children}</Box>
        </CustomList>
    );
};

export const ServiceRequestList = () => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const { data, isLoading: isLoadingIdentity } = useGetIdentity();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const userEmail = data?.id;
    const isServiceRegular = permissions.isService && !permissions.isServiceMaster;
    const listFilter = isServiceRegular ? { 'serviceman.email': userEmail, withAggregates: true } : { withAggregates: true };

    const handleRowClick = id => {
        openDrawer(id, 'service-request-edit');
        return false;
    };

    const filters = useMemo(
        () => (permissions.isAdmin || permissions.isServiceMaster ? getAllFilters(isMobile, translate) : getServiceRegularFilters(isMobile, translate)),
        [isMobile, permissions.isAdmin, permissions.isServiceMaster, translate],
    );

    if (isLoading || isLoadingIdentity) return null;

    const bulkActionButtons =
        permissions.isAdmin || permissions.isService ? <ServiceBulkActionButtons buttons={[<ServiceBulkActionForm key='bulk-form' />]} /> : false;

    return (
        <StyledServiceList
            filters={[{ ...searchBar, props: { ...searchBar.props, label: translate('common.search') } }, ...filters]}
            filterDefaultValues={
                !isMobile
                    ? {
                          status_in: ['new', 'failedContact', 'accepted', 'pending', 'inProgress', 'fixed'],
                      }
                    : undefined
            }
            sort={{ field: 'createdAt', order: 'DESC' }}
            actions={<ListActions />}
            filter={listFilter}
            paginationClassName='pagination-rounded-bottom'
        >
            <CustomDatagridConfigurable
                header={<CustomDatagridHeader />}
                bulkActionButtons={bulkActionButtons}
                rowClick={isDesktop ? handleRowClick : false}
                className='responsive-table'
            >
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('resources.serviceRequest.labels.requestTitle')}
                            sortByLabel={translate('common.priorityLabel')}
                            subTitle={translate('resources.serviceRequest.labels.requestSubtitle')}
                        />
                    }
                    sortBy='priority'
                    render={record => <ServiceRequestIdField record={record} resource='service-request' />}
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('common.collectionPoint')}
                            subTitle={translate('resources.serviceRequest.labels.collectionPointSubtitle')}
                        />
                    }
                    sortBy='collectionPoint.customId'
                    render={record => <DeviceCollectionPointField collectionPoint={record.collectionPoint} />}
                />
                <FunctionField
                    className='service-request-serial-number'
                    label={<TableHeaderCellWithSubtitle title={translate('resources.device.fields.serialNumber')} />}
                    source='device.serialNumber'
                    sortBy='device.serialNumber'
                    onClick={e => e.stopPropagation()}
                    render={record => <DrawerLink resource='device' action='show' source={record.device.id} label={record.device.serialNumber} relative />}
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('common.status')} subTitle={translate('resources.product.fields.lastUpdate')} />}
                    sortBy='updatedAt'
                    render={record =>
                        record.status === 'new' ? (
                            <DeviceServiceStatusField
                                className='device-status-field device-status-field-last service-request-device-status-field'
                                formatSince={record.updatedAt}
                                statusText={translate('resources.serviceRequest.labels.statusNew')}
                                handleClick={() => handleRowClick(record.id)}
                            />
                        ) : (
                            <ServiceRequestStatusField record={record} handleClick={() => handleRowClick(record.id)} />
                        )
                    }
                />
                {isDesktop && (
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.serviceRequest.labels.deviceStateTitle')}
                                subTitle={translate('resources.serviceRequest.labels.deviceStateSubtitle')}
                            />
                        }
                        sortBy='device.state'
                        render={record => <DeviceServiceRequestStateField record={record} />}
                    />
                )}
                {isDesktop && (
                    <FunctionField
                        label={translate('resources.serviceRequest.labels.causeTitle')}
                        sortBy='error.code'
                        render={record => <DeviceIssueErrorField className='device-status-field device-error-field' record={record} />}
                    />
                )}
                {isDesktop && (
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.serviceRequest.labels.coordinator')}
                                subTitle={translate('resources.serviceRequest.labels.serviceman')}
                            />
                        }
                        sortBy='coordinator.lastName'
                        render={record => <ServiceRequestUsersField record={record} />}
                    />
                )}
                {isDesktop && (
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.serviceRequest.labels.sourceTitle')}
                                subTitle={translate('common.createdAt')}
                            />
                        }
                        sortBy='createdAt'
                        render={record => <ServiceRequestSourceField record={record} />}
                    />
                )}
            </CustomDatagridConfigurable>
        </StyledServiceList>
    );
};

const StyledServiceRequestCreate = styled(Box)(globalStyles.serviceForm);

const DEVICE_RESOURCES_FOR_SERVICE_CREATE = new Set(['device', 'device-issue']);

export const ServiceRequestCreate = ({ handleClose = () => {}, elementId }) => {
    const resource = useResourceContext();
    const shouldFetchDevice = !!elementId && DEVICE_RESOURCES_FOR_SERVICE_CREATE.has(resource);
    const { data, isLoading } = useGetOne('device', { id: elementId }, { enabled: shouldFetchDevice });
    const translate = useTranslate();

    const recordFromDevice = data ? { collectionPointId: data.collectionPointId ?? data.collectionPoint?.id, deviceId: data.id } : {};

    if (isLoading) return null;

    return (
        <StyledServiceRequestCreate>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.serviceRequest.labels.createServiceRequestTitle')}</Typography>
                    <Box>
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <CustomCreate handleClose={handleClose} record={recordFromDevice} resource='service-request'>
                <Box className='padding-wrapper'>
                    <ServiceForm resource='service-request' />
                </Box>
            </CustomCreate>
        </StyledServiceRequestCreate>
    );
};

export const ServiceRequestEdit = ({ handleClose = () => {}, elementId }) => {
    const { data, isLoading } = useGetOne('service-request', { id: elementId });
    const translate = useTranslate();

    const formattedId = data?.customId?.toString().padStart(6, '0') || '';

    if (isLoading) return null;

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.serviceRequest.labels.editServiceRequestTitle', { id: formattedId })}>
            <Edit
                id={elementId}
                resource='service-request'
                actions={false}
                mutationMode='pessimistic'
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <ServiceShow handleClose={handleClose} elementId={elementId} resource='service-request' />
            </Edit>
        </RightDrawer>
    );
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.serviceRequest.labels.createServiceRequest')} />
                    {permissions.isAdmin && <CachedDataActionButton resource='device' type='Analytics' />}
                    {permissions.isAdmin && <CachedDataActionButton resource='device-service' type='Service' />}
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const ServiceRequestSourceField = ({ record }) => {
    const translate = useTranslate();
    let source = '';
    if (record.sourceType === 'auto') {
        source = translate('selectOptions.serviceRequestSourceTypeChoices.auto');
    } else if (record.sourceType === 'collection_point') {
        source = translate('selectOptions.serviceRequestSourceTypeChoices.collectionPoint');
    } else if (record.sourceType === 'user') {
        source = `${record.source.firstName} ${record.source.lastName}`;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', whiteSpace: 'nowrap' }}>
            <span>{source}</span>
            <span style={{ fontSize: 11 }}>
                {record?.createdAt ? (
                    <Tooltip
                        title={formatIntervalSince(record.createdAt) ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}` : ''}
                        arrow
                        classes={{ tooltip: 'tooltip-black' }}
                    >
                        <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                    </Tooltip>
                ) : (
                    ''
                )}
            </span>
        </div>
    );
};

export const UserField = ({ record, relation }) => {
    const relationIdField = `${relation}Id`;
    return record[relationIdField] ? (
        <span>
            {record[relation].firstName} {record[relation].lastName}
        </span>
    ) : (
        <UnassignedField />
    );
};

const CollectionPointModel = PropTypes.shape({
    id: PropTypes.string.isRequired,
    contractorId: PropTypes.string.isRequired,
    serviceCompanyId: PropTypes.string,
    contractor: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    customId: PropTypes.string.isRequired,
    premiumService: PropTypes.bool.isRequired,
    address: PropTypes.shape({
        address: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        postalCode: PropTypes.string.isRequired,
        country: PropTypes.string.isRequired,
    }).isRequired,
    contact: PropTypes.shape({
        contactPhoneNumber: PropTypes.string.isRequired,
    }).isRequired,
});

const DeviceModel = PropTypes.shape({
    id: PropTypes.string.isRequired,
    serialNumber: PropTypes.string.isRequired,
    state: PropTypes.string,
    collectionPointId: PropTypes.string,
    collectionPoint: CollectionPointModel,
});

const UserModel = PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
});

const ServiceRequestModel = PropTypes.shape({
    id: PropTypes.string.isRequired,
    customId: PropTypes.number.isRequired,
    deviceId: PropTypes.string.isRequired,
    device: DeviceModel.isRequired,
    sourceType: PropTypes.string.isRequired,
    sourceId: PropTypes.string,
    source: PropTypes.object,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    description: PropTypes.string,
    coordinatorId: PropTypes.string,
    coordinator: UserModel,
    servicemanId: PropTypes.string,
    serviceman: UserModel,
    workingTime: PropTypes.number.isRequired,
    visibleChangeCount: PropTypes.number,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
});

ServiceRequestSourceField.propTypes = {
    record: ServiceRequestModel,
};

ServiceRequestCreate.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

ServiceRequestEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

UserField.propTypes = {
    record: ServiceRequestModel,
    relation: PropTypes.oneOf(['coordinator', 'serviceman']),
};

StyledServiceList.propTypes = {
    children: PropTypes.node.isRequired,
};
